/** Canonical platform display names shared across web and mobile surfaces. */
export const SOCIAL_PLATFORM_LABELS = {
    instagram: "Instagram",
    twitter: "X / Twitter",
};
/** Max characters for the custom website link label in manager wizard. */
export const WEBSITE_DISPLAY_LABEL_MAX_LENGTH = 28;
/**
 * Normalizes a raw social URL or handle into a parseable URL string.
 */
function normalizeSocialUrl(raw) {
    const trimmed = raw.trim();
    if (!trimmed)
        return "";
    if (trimmed.startsWith("@"))
        return trimmed;
    if (/^https?:\/\//i.test(trimmed))
        return trimmed;
    return `https://${trimmed}`;
}
/**
 * Strips scheme and www prefix for website URL input fields (manager wizard).
 */
export function stripWebsiteUrlForInput(url) {
    return url.trim().replace(/^https?:\/\//i, "").replace(/^www\./i, "");
}
/**
 * Builds a full https URL from wizard input (domain/path without scheme).
 */
export function buildWebsiteUrlFromInput(input) {
    const trimmed = input.trim();
    if (!trimmed)
        return "";
    if (/^https?:\/\//i.test(trimmed))
        return trimmed;
    return `https://${trimmed}`;
}
/**
 * Formats a website URL for display when no custom label is set.
 */
export function formatWebsiteUrlForDisplay(url) {
    try {
        const parsed = new URL(normalizeSocialUrl(url));
        const host = parsed.hostname.replace(/^www\./, "");
        const path = parsed.pathname === "/" ? "" : parsed.pathname;
        return `${host}${path}`;
    }
    catch {
        return url.replace(/^https?:\/\//i, "").replace(/^www\./i, "");
    }
}
/**
 * Strips a stored social URL or @handle down to the bare username for wizard input
 * (the visible @ prefix is rendered separately via startAdornment).
 */
export function stripSocialHandleForInput(url) {
    return extractSocialHandle(url).replace(/^@/, "");
}
/**
 * Builds a canonical Instagram profile URL from wizard handle input.
 */
export function buildInstagramUrlFromHandle(input) {
    const handle = input.trim().replace(/^@/, "").split(/[/?#]/)[0]?.trim();
    if (!handle)
        return "";
    return `https://instagram.com/${handle}`;
}
/**
 * Builds a canonical X/Twitter profile URL from wizard handle input.
 */
export function buildTwitterUrlFromHandle(input) {
    const handle = input.trim().replace(/^@/, "").split(/[/?#]/)[0]?.trim();
    if (!handle)
        return "";
    return `https://x.com/${handle}`;
}
/**
 * Extracts an @handle from a social profile URL or raw @handle input.
 */
export function extractSocialHandle(url) {
    const trimmed = url.trim();
    if (!trimmed)
        return "@";
    if (trimmed.startsWith("@")) {
        const handle = trimmed.slice(1).split(/[/?#]/)[0]?.trim();
        return handle ? `@${handle}` : "@";
    }
    try {
        const parsed = new URL(normalizeSocialUrl(trimmed));
        const segments = parsed.pathname.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);
        if (segments.length > 0) {
            const reserved = new Set(["p", "reel", "reels", "stories", "status", "i", "intent"]);
            const first = segments[0];
            // Skip reserved path prefixes only when a following segment is the real handle
            // (e.g. /p/ABC → @ABC). A lone reserved segment is a valid username (e.g. /i → @i).
            if (reserved.has(first.toLowerCase()) && segments.length > 1) {
                return `@${segments[1]}`;
            }
            return `@${first}`;
        }
        const host = parsed.hostname.replace(/^www\./, "").split(".")[0];
        if (host)
            return `@${host}`;
    }
    catch {
        const stripped = trimmed.replace(/^@/, "").split(/[/?#]/)[0]?.trim();
        if (stripped)
            return `@${stripped}`;
    }
    return "@";
}
/**
 * Returns display text for the website link block (custom label or truncated URL via CSS).
 */
export function formatWebsiteLinkDisplayValue(href, displayLabel) {
    const label = displayLabel?.trim();
    if (label)
        return label;
    return formatWebsiteUrlForDisplay(href);
}
/**
 * Returns the display text for a social/web link block on event detail surfaces.
 */
export function formatSocialLinkDisplayValue(kind, href, options) {
    if (kind === "website") {
        return formatWebsiteLinkDisplayValue(href, options?.websiteDisplayLabel);
    }
    return extractSocialHandle(href);
}
